/**
 * Central analytics utility — all events routed through GTM dataLayer.
 * GTM tags handle forwarding to Mixpanel, GA4, Facebook, etc.
 */
import * as Sentry from '@sentry/angular';

declare global {
  interface Window {
    dataLayer?: Record<string, any>[];
  }
}

/**
 * Values treated as "empty" and stripped before sending to GTM dataLayer.
 * Falsy values like 0 and false are intentionally preserved.
 */
const INVALID_VALUES: ReadonlySet<unknown> = new Set([undefined, null, 'undefined', 'null', '']);

function sanitizeProperties(data: Record<string, any>): Record<string, any> {
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!INVALID_VALUES.has(value)) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

// 이벤트명당 1회만 보고한다 — window.dataLayer 가 덮이면 이후 모든 push 가 던진다.
const reportedPushFailures = new Set<string>();

// webpack ESM 네임스페이스는 spyOn 이 안 되므로(configurable:false) 보고 지점을 객체 메서드로 둔다.
export const analyticsErrorReporter = {
  capture(error: unknown, analyticsEvent: string, propertyKeys: string[]): void {
    Sentry.captureException(error, {
      tags: { feature: 'analytics', phase: 'dataLayer_push' },
      extra: { analyticsEvent, propertyKeys },
    });
  },
};

function pushDataLayer(data: Record<string, any>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  const cleaned = sanitizeProperties(data);
  // `dataLayer.push` 는 GTM 이 바꿔치기하는 서드파티 코드다. 여기서 던지면 호출부가 이미 성공시킨
  // 결제·취소가 실패로 처리된다. 삼키는 순간 Sentry 자동 포착 경로가 끊기므로 직접 올린다.
  try {
    window.dataLayer.push(cleaned);
  } catch (error) {
    const eventName = typeof cleaned['event'] === 'string' ? cleaned['event'] : '(no event key)';
    if (reportedPushFailures.has(eventName)) return;
    reportedPushFailures.add(eventName);
    analyticsErrorReporter.capture(error, eventName, Object.keys(cleaned));
  }
}

export function track(event: string, properties?: Record<string, any>): void {
  pushDataLayer({ ...(properties ?? {}), event });
}

/** `trackAdminPurchaseComplete`가 읽는 결제 생성 응답의 최소 형태. */
interface AdminPaymentResponse {
  id: number;
  totalPrice?: number;
  order?: {
    point?: number;
    coupon?: { name?: string };
    product?: { id?: number; title?: string };
  };
}

/**
 * 어드민 결제 등록(직접 결제 / 누락 결제)에서 발화하는 `purchase_complete`.
 *
 * 이 이벤트는 **담당자 브라우저에서 담당자 쿠키로** 나간다. 그래서 GTM 광고 전환 태그가
 * 그대로 타면 실제 구매자가 아니라 담당자 세션에 전환이 귀속되고, 담당자가 자사 광고를
 * 클릭한 적이 있으면(`_fbc`는 90일 유지) 그 광고의 성과로 잡힌다. 리포트 오염에 그치지
 * 않고 자동입찰이 이 가짜 전환을 학습한다.
 *
 * 그래서 이벤트 자체는 유지하되(Mixpanel 내부 매출 집계가 쓴다) `order_channel: 'admin'`을
 * 달아, GTM에서 광고 태그만 이 값으로 제외하도록 한다. 값을 바꾸면 GTM 트리거 조건도
 * 함께 고쳐야 한다.
 */
export function trackAdminPurchaseComplete(resp: AdminPaymentResponse): void {
  const point = resp.order?.point;

  track('purchase_complete', {
    order_channel: 'admin',
    // GA4 transactionId dimension이 문자열이라 숫자로 보내면 결제 건과의 조인이 깨진다.
    transaction_id: String(resp.id),
    product_name: resp.order?.product?.title,
    product_id: resp.order?.product?.id,
    total_price: resp.totalPrice,
    currency: 'KRW',
    coupon_name: resp.order?.coupon?.name,
    point: typeof point === 'number' ? point : undefined,
  });
}

export function identifyUser(user: {
  id: number;
  email?: string;
  name?: string;
  nickname?: string;
  provider?: string;
  gender?: string;
  [key: string]: any;
}): void {
  pushDataLayer({
    event: 'identify_user',
    user_id: user.id,
    user_email: user.email,
    user_name: user.name,
    user_nickname: user.nickname,
    user_provider: user.provider,
    user_gender: user.gender,
  });
}

export function resetIdentity(): void {
  pushDataLayer({ event: 'reset_identity' });
}

export function pushUserData(user: { id: number; provider?: string; gender?: string }): void {
  pushDataLayer({
    event: 'user_data',
    user_id: user.id,
    user_provider: user.provider,
    user_gender: user.gender,
  });
}
